export type NetsapiensAccountSource = "EDGE" | "VPS" | "MCU";

export const VALID_NETSAPIENS_SOURCES: readonly NetsapiensAccountSource[] = Object.freeze(["EDGE", "VPS", "MCU"]);

export interface NetsapiensAccountEntry {
  source: NetsapiensAccountSource;
  domain: string;
  accountNumber: string;
  autotaskCompanyId: number | null;
  onebillAccountName: string;
  matchedBy: string;
}

export interface NetsapiensAccountsDoc {
  updatedAt: string;
  accounts: NetsapiensAccountEntry[];
}

export function validateNetsapiensAccountsDoc(data: unknown): NetsapiensAccountsDoc {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid accounts document: expected an object");
  }
  const raw = data as Record<string, unknown>;

  if (
    typeof raw.updatedAt !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(raw.updatedAt.trim()) ||
    Number.isNaN(Date.parse(raw.updatedAt.trim()))
  ) {
    throw new Error("Invalid accounts document: updatedAt must be a valid canonical ISO-8601 UTC date string");
  }

  if (!Array.isArray(raw.accounts)) {
    throw new Error("Invalid accounts document: accounts must be an array");
  }

  const seenKeys = new Set<string>();
  const accounts: NetsapiensAccountEntry[] = [];

  for (let i = 0; i < raw.accounts.length; i++) {
    const entry = raw.accounts[i];
    if (!entry || typeof entry !== "object") {
      throw new Error(`Invalid accounts document: accounts[${i}] must be an object`);
    }
    const acc = entry as Record<string, unknown>;

    if (typeof acc.source !== "string" || !VALID_NETSAPIENS_SOURCES.includes(acc.source as NetsapiensAccountSource)) {
      throw new Error(`Invalid accounts document: accounts[${i}].source must be EDGE, VPS, or MCU`);
    }
    const source = acc.source as NetsapiensAccountSource;

    if (typeof acc.domain !== "string" || !acc.domain.trim()) {
      throw new Error(`Invalid accounts document: accounts[${i}].domain must be a non-empty string`);
    }
    const domain = acc.domain.trim();

    if (typeof acc.accountNumber !== "string" || !acc.accountNumber.trim()) {
      throw new Error(`Invalid accounts document: accounts[${i}].accountNumber must be a non-empty string`);
    }
    if (/[\r\n]/.test(acc.accountNumber)) {
      throw new Error(`Invalid accounts document: accounts[${i}].accountNumber must be a single-line identifier`);
    }
    const accountNumber = acc.accountNumber.trim();

    if (acc.autotaskCompanyId !== null && (typeof acc.autotaskCompanyId !== "number" || !Number.isInteger(acc.autotaskCompanyId))) {
      throw new Error(`Invalid accounts document: accounts[${i}].autotaskCompanyId must be an integer or null`);
    }
    const autotaskCompanyId = acc.autotaskCompanyId as number | null;

    if (typeof acc.onebillAccountName !== "string" || !acc.onebillAccountName.trim()) {
      throw new Error(`Invalid accounts document: accounts[${i}].onebillAccountName must be a non-empty string`);
    }
    const onebillAccountName = acc.onebillAccountName.trim();

    if (typeof acc.matchedBy !== "string" || !acc.matchedBy.trim()) {
      throw new Error(`Invalid accounts document: accounts[${i}].matchedBy must be a non-empty string`);
    }
    const matchedBy = acc.matchedBy.trim();

    const key = `${source}/${domain}`;
    if (seenKeys.has(key)) {
      throw new Error(`Invalid accounts document: duplicate account for (${source}, ${domain})`);
    }
    seenKeys.add(key);

    accounts.push({
      source,
      domain,
      accountNumber,
      autotaskCompanyId,
      onebillAccountName,
      matchedBy,
    });
  }

  return {
    updatedAt: raw.updatedAt.trim(),
    accounts,
  };
}

export const NETSAPIENS_ACCOUNTS_DOC_PATH = "settings/onebill-netsapiens-accounts";
